import { useEffect, useState } from "react";
import { MessageProfilePictureRelative } from "./MessageProfilePicture";

/**
 * The Order component is a functional component that displays a single order on the phone screen
 * @param {Object} props - the props passed down to this component
 * @param {String} props.name - the name of the character
 * @param {Integer} props.friendshipScore - the friendship score of the character
 * @param {String} props.text - the text of the order
 * @param {Boolean} props.isAlternate - boolean to determine if the order is an alternate order -for styling purposes
 * @param {Function} props.orderCallback - the function to call when the order is clicked
 * @param {String} props.colourPallete - the colour pallete to be used - normal or blackAndwhite
 * @param {Object} props.accessibilitySettings - the accessibility settings
 * @returns
 */
function Order({
	name,
	friendshipScore,
	text = "",
	isAlternate,
	orderCallback,
	colourPallete,
	accessibilitySettings,
	activityData,
	index,
}) {
	return (
		<button
			type="button"
			onClick={orderCallback}
			id="single-order__container"
			className={`w-full h-auto border-none box-border flex items-center p-[0.625rem] px-[0.8125rem] gap-[0.625rem] self-stretch cursor-pointer ${
				isAlternate ? "bg-[#DDDFE2]" : "bg-[#FFFFFFFF]"
			}`}
			aria-label={`Order from ${name} with a friendship score of ${friendshipScore}`}
		>
			{/* Profile picture with relative positioning */}
			<div className="w-[15%] lg:w-[20%] min-w-[4.2rem] flex justify-center items-center h-full">
				<MessageProfilePictureRelative
					name={name}
					friendshipScore={friendshipScore}
					friendshipStarOrientation={"left"}
					colourPallete={colourPallete}
				/>
			</div>

			<div
				id="phone-ui__order__text-box"
				className="w-[85%] lg:80% h-[5.125rem]  overflow-hidden text-ellipsis text-left"
			>
				{/* User name */}
				<h3
					id="phone-ui__order__title"
					className="text-[1.125rem] font-bold leading-none text-black   h-[1.5rem] flex-shrink-0 flex items-center"
				>
					{name}
					{!activityData.studentData?.read[index] && (
						<span
							className="inline-block w-2 h-2 bg-red-500 items-center rounded-full ml-2"
							style={{ backgroundColor: accessibilitySettings.display.blackAndWhite ? "#000000" : null }}
						/>
					)}
				</h3>
				{/* The order text - uses Tailwind plugin called lineclamp for clamping the number of lines display */}
				<p
					id="phone-ui__order__text"
					className="text-black overflow-hidden text-ellipsis text-[1rem] font-medium leading-[150%] h-[3rem] line-clamp-2"
					dangerouslySetInnerHTML={{ __html: text }}
				/>
			</div>
		</button>
	);
}

/**
 * The OrdersTab component is a functional component that displays the orders on the phone screen
 * @param {Object} props - the props passed down to this component
 * @param {Array} props.orders - the orders to be displayed
 * @param {Function} props.setCurrentScreen - the function to set the current screen which is displayed
 * @param {Function} props.setTitleText - the function to set the title text for the header
 * @param {Function} props.setTitleImageSrc - the function to set the title image source for the header
 * @param {Function} props.setFriendshipScore - the function to set the friendship score of the character for the order
 * @param {Function} props.setOrderTextID - the function to set the order text ID
 * @param {String} props.colourPallete - the colour pallete to be used - normal or blackAndwhite
 * @param {Function} props.playSound - the function to play a sound - used for 'click' sound when an order is clicked
 * @param {Object} props.accessibilitySettings - the accessibility settings
 * @param {Function} props.setSelectedCharacter - the function to set the selected character
 * @returns
 */
function OrdersTab({
	orders,
	setCurrentScreen,
	setTitleText,
	setTitleImageSrc,
	setFriendshipScore,
	setOrderTextID,
	colourPallete,
	playSound,
	accessibilitySettings,
	setSelectedCharacter,
	activityData,
	setStudentActivityAttribute,
}) {
	// Handle when an order is clicked
	const handleOrderClick = (name, friendshipScore, id) => {
		setCurrentScreen("orders-message");
		setSelectedCharacter(name);
		setTitleText(null);
		setTitleImageSrc(name);
		setFriendshipScore(friendshipScore);
		setOrderTextID(id);
		const read = activityData.studentData?.read;
		read[id] = true;
		setStudentActivityAttribute("read", read);
		playSound("click");
	};

	return (
		<div>
			{orders.map((order, index) => (
				<Order
					key={order.name}
					name={order.name}
					friendshipScore={order.friendshipScore}
					text={order.text}
					isAlternate={index % 2 !== 0} // For alternate styling
					orderCallback={() => handleOrderClick(order.name, order.friendshipScore, index)}
					colourPallete={colourPallete} // black and white or colour
					accessibilitySettings={accessibilitySettings}
					activityData={activityData}
					index={index}
				/>
			))}
		</div>
	);
}

export default OrdersTab;
